import { useForm } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog'
import { Label } from '@/shared/ui/label'
import { Textarea } from '@/shared/ui/textarea'
import { t } from '@/shared/lib/i18n'

type RetireLotDetailDialogProps = {
  isOpen: boolean
  closeDialogCallback: () => void
  retireSubmissionPathForLotDetail: string
  afterSuccessfulSubmissionCallback?: () => void
}

const coerceInboundErrorsToRenderableLinesList = (value: unknown): string[] => {
  if (typeof value === 'string' && value.length > 0) return [value]
  if (!Array.isArray(value)) return []
  return value.filter((candidateLine): candidateLine is string => typeof candidateLine === 'string')
}

/** Retire PATCH uses nested `instrument_lot_log.retirement_comment` (strong params parity with Rails). */
export const RetireLotDetailDialog = ({
  isOpen,
  closeDialogCallback,
  retireSubmissionPathForLotDetail,
  afterSuccessfulSubmissionCallback,
}: RetireLotDetailDialogProps) => {
  const retireLotDetailForm = useForm({
    instrument_lot_log: { retirement_comment: '' },
  })

  const formInboundErrorsKeyedByField = retireLotDetailForm.errors as Record<string, unknown>

  /** `CamelizesInertiaProps` lower-camels inertia error keys (`retirementComment`). */
  const retirementCommentRenderableErrorLinesFlattenedUnique = Array.from(
    new Set([
      ...coerceInboundErrorsToRenderableLinesList(formInboundErrorsKeyedByField.retirementComment),
      ...coerceInboundErrorsToRenderableLinesList(formInboundErrorsKeyedByField.retirement_comment),
      ...coerceInboundErrorsToRenderableLinesList(
        formInboundErrorsKeyedByField['instrumentLotLog.retirementComment'],
      ),
      ...coerceInboundErrorsToRenderableLinesList(formInboundErrorsKeyedByField.instrument_lot_log),
    ]),
  )

  const retirementCommentEnteredTrimmed =
    retireLotDetailForm.data.instrument_lot_log.retirement_comment.trim()

  const handleSubmitConfirmRetireLotDetail = () => {
    retireLotDetailForm.patch(retireSubmissionPathForLotDetail, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => {
        closeDialogCallback()
        afterSuccessfulSubmissionCallback?.()
      },
    })
  }

  return (
    <AlertDialog
      open={isOpen}
      onOpenChange={(nextOpenSignal) => {
        if (!nextOpenSignal) {
          closeDialogCallback()
          retireLotDetailForm.reset()
          retireLotDetailForm.clearErrors()
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('instruments.lotsRetire.dialogTitle')}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-left text-muted-foreground">
              <p className="text-foreground">{t('instruments.lotsRetire.dialogLead')}</p>
              <p className="text-foreground font-medium">
                {t('instruments.lotsRetire.dialogNote')}
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2 py-2">
          <Label htmlFor="lot-detail-retirement-comment" className="sr-only">
            {t('instruments.lotsRetire.commentsLabel')}
          </Label>
          <Textarea
            id="lot-detail-retirement-comment"
            placeholder={t('instruments.lotsRetire.commentsPlaceholder')}
            value={retireLotDetailForm.data.instrument_lot_log.retirement_comment}
            disabled={retireLotDetailForm.processing}
            onChange={(changeEvent) =>
              retireLotDetailForm.setData(
                'instrument_lot_log.retirement_comment',
                changeEvent.target.value,
              )
            }
            rows={4}
          />
          {retirementCommentRenderableErrorLinesFlattenedUnique.map((singleRenderableErrorLine) => (
            <p key={singleRenderableErrorLine} className="text-sm text-destructive">
              {singleRenderableErrorLine}
            </p>
          ))}
          {formInboundErrorsKeyedByField.base ? (
            <p className="text-sm text-destructive">{String(formInboundErrorsKeyedByField.base)}</p>
          ) : null}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel type="button" disabled={retireLotDetailForm.processing}>
            {t('instruments.lotsRetire.cancelButton')}
          </AlertDialogCancel>
          <Button
            type="button"
            variant="default"
            disabled={
              retireLotDetailForm.processing || retirementCommentEnteredTrimmed.length === 0
            }
            onClick={handleSubmitConfirmRetireLotDetail}
          >
            {t('instruments.lotsRetire.confirmButton')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
